import React, { useState, useRef } from "react";
import { storage, db } from "../firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

function UploadZone() {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [dragOver, setDragOver] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [guestName, setGuestName] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("Przyjęcie");
    const [pendingFiles, setPendingFiles] = useState(null);
    const inputRef = useRef(null);
    // Fix dla iOS Safari
    const handleClick = () => {
        if (inputRef.current) {
            inputRef.current.value = "";
            inputRef.current.click();
        }
    };

    const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".heic", ".heif", ".webp", ".bmp", ".tiff"];
    const RAW_EXTENSIONS = [".raw", ".dng", ".cr2", ".cr3", ".nef", ".arw", ".orf", ".raf", ".rw2"];
    const VIDEO_EXTENSIONS = [".mp4", ".mov", ".avi", ".mkv", ".wmv", ".flv", ".3gp", ".m4v", ".webm"];

    const getFileType = (file) => {
        const ext = "." + file.name.split(".").pop().toLowerCase();
        if (RAW_EXTENSIONS.includes(ext)) return "raw";
        if (IMAGE_EXTENSIONS.includes(ext)) return "image";
        if (VIDEO_EXTENSIONS.includes(ext)) return "video";
        if (file.type.startsWith("image/")) return "image";
        if (file.type.startsWith("video/")) return "video";
        return null;
    };

    const handleFiles = (files) => {
        if (!files || files.length === 0) return;
        setPendingFiles(files);
        setShowModal(true);
    };

    const handleConfirm = () => {
        const name = guestName.trim() || "Gość";
        setShowModal(false);
        setGuestName("");
        startUpload(pendingFiles, name, selectedCategory);
        if (inputRef.current) inputRef.current.value = "";
    };

    const startUpload = async (files, author, category) => {
        if (!files || files.length === 0) return;
        setUploading(true);

        const fileArray = Array.from(files);

        for (const file of fileArray) {
            const fileType = getFileType(file);

            console.log("Plik:", file.name, "| Typ:", file.type, "| Wykryty:", fileType);

            if (fileType === "raw") {
                showToast(`❌ Format RAW nie jest obsługiwany! Zmień ustawienia aparatu na JPG 📷`);
                continue;
            }

            if (!fileType) {
                showToast(`❌ Nieobsługiwany format: ${file.name} — dozwolone tylko zdjęcia i filmiki!`);
                continue;
            }

            const isImage = fileType === "image";
            const isVideo = fileType === "video";

            if (isImage && file.size > 20 * 1024 * 1024) {
                showToast(`❌ Zdjęcie za duże! Max 20MB`);
                continue;
            }

            if (isVideo && file.size > 1024 * 1024 * 1024) {
                showToast(`❌ Filmik za duży! Max 1GB`);
                continue;
            }

            // Timestamp taki sam dla Storage i Firestore
            const timestamp = Date.now();
            const folder = isVideo ? "videos" : "photos";
            const fileNameWithTimestamp = `${timestamp}_${file.name}`;
            console.log("fileNameWithTimestamp:", fileNameWithTimestamp);
            const storagePath = `${folder}/${fileNameWithTimestamp}`;
            const storageRef = ref(storage, storagePath);

            // Ustaw contentType ręcznie dla HEIC/HEIF
            const ext = "." + file.name.split(".").pop().toLowerCase();
            let contentType = file.type;
            if (!contentType || contentType === "") {
                if (ext === ".heic" || ext === ".heif") contentType = "image/heic";
                else if (ext === ".webp") contentType = "image/webp";
                else if (ext === ".mov") contentType = "video/quicktime";
                else contentType = isVideo ? "video/mp4" : "image/jpeg";
            }

            const uploadTask = uploadBytesResumable(storageRef, file, {
                contentType: contentType,
            });

            showToast(`⏳ Wysyłanie ${isVideo ? "filmiku" : "zdjęcia"}...`);

            await new Promise((resolve, reject) => {
                uploadTask.on(
                    "state_changed",
                    (snapshot) => {
                        const pct = Math.round(
                            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                        );
                        setProgress(pct);
                    },
                    (error) => {
                        console.error("Błąd uploadu:", error);
                        reject(error);
                    },
                    async () => {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        await addDoc(collection(db, "photos"), {
                            url: downloadURL,
                            // fileName z timestamp — identyczny jak w Storage!
                            fileName: fileNameWithTimestamp,
                            uploadedAt: serverTimestamp(),
                            author: author,
                            category: category,
                            type: isVideo ? "video" : "image",
                        });
                        resolve();
                    }
                );
            });
        }

        setUploading(false);
        setProgress(0);

        const validFiles = fileArray.filter(file => {
            const ft = getFileType(file);
            return ft && ft !== "raw";
        });

        if (validFiles.length > 0) {
            showToast(`✓ Dodano! Dzięki ${author}! 🎉`);
        }

        if (inputRef.current) inputRef.current.value = "";
    };

    const showToast = (msg) => {
        const existing = document.querySelector(".toast");
        if (existing) existing.remove();
        const toast = document.createElement("div");
        toast.className = "toast";
        toast.textContent = msg;
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 4000);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
    };

    return (
        <>
            {/* MODAL */}
            {showModal && (
                <div className="name-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="name-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="name-modal-icon">👋</div>
                        <h3 className="name-modal-title">Dodaj wspomnienia</h3>
                        <p className="name-modal-sub">
                            Powiedz nam jak masz na imię i wybierz kategorię 😊
                        </p>

                        <input
                            className="name-modal-input"
                            type="text"
                            placeholder="Wpisz swoje imię..."
                            value={guestName}
                            onChange={(e) => setGuestName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
                            autoFocus
                            maxLength={30}
                        />

                        <div className="name-modal-category">
                            <p className="name-modal-category-label">Kategoria:</p>
                            <div className="name-modal-category-btns">
                                {[
                                    { name: "Ceremonia", icon: "⛪" },
                                    { name: "Przyjęcie", icon: "🥂" },
                                    { name: "Inne", icon: "📸" },
                                ].map((cat) => (
                                    <button
                                        key={cat.name}
                                        className={`category-btn ${selectedCategory === cat.name ? "active" : ""}`}
                                        onClick={() => setSelectedCategory(cat.name)}
                                    >
                                        <span className="cat-icon">{cat.icon}</span>
                                        {cat.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="name-modal-btns">
                            <button className="name-modal-confirm" onClick={handleConfirm}>
                                ✓ Dodaj
                            </button>
                            <button
                                className="name-modal-skip"
                                onClick={() => {
                                    setShowModal(false);
                                    setGuestName("");
                                    setPendingFiles(null);
                                    if (inputRef.current) inputRef.current.value = "";
                                }}
                            >
                                Anuluj
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* STREFA UPLOADU */}
            <div className="upload-wrap">
                <div
                    className={`drop-zone ${dragOver ? "over" : ""}`}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => handleClick()}
                >
                    <input
                        ref={inputRef}
                        type="file"
                        multiple
                        accept="image/*,video/*,.heic,.heif"
                        style={{ display: "none" }}
                        onChange={(e) => {
                            if (e.target.files?.length > 0) {
                                handleFiles(e.target.files);
                            }
                        }}
                    />

                    {uploading ? (
                        <>
                            <span className="drop-icon">⏳</span>
                            <span className="drop-text">Wysyłanie...</span>
                            <div className="progress-bar">
                                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                            </div>
                            <span className="drop-sub">{progress}%</span>
                        </>
                    ) : (
                        <>
                            <span className="drop-icon">📷🎥</span>
                            <span className="drop-text">Dodaj zdjęcia lub filmiki</span>
                            <span className="drop-sub">
                Zdjęcia max 20MB · Filmiki max 1GB (ok. 3 min w 4K)
              </span>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default UploadZone;